/**
 * useTimelineDragPan Hook
 *
 * Enables drag-to-pan functionality for timeline navigation.
 * Allows clicking and dragging to scroll both horizontally and vertically.
 * Releases with momentum that decelerates smoothly.
 * Reports when the scroll has hit horizontal extents.
 */

import { useState, useCallback, useRef, useEffect } from 'react'

const FRICTION = 0.7          // Velocity decay per frame (~0.2s coast at 60fps)
const MIN_VELOCITY = 0.5       // px/frame threshold to stop momentum
const VELOCITY_SAMPLE_MS = 80  // Window of mouse history used to compute release velocity

/**
 * useTimelineDragPan - Drag-to-pan handler with momentum and edge detection
 *
 * @param {Object} params
 * @param {Object} params.contentRef - Content container ref
 *
 * @returns {Object} {
 *   isDragging,
 *   handleMouseDown,
 *   cursor,
 *   atLeftEdge,   - true when scroll is at left extent during drag/momentum
 *   atRightEdge,  - true when scroll is at right extent during drag/momentum
 * }
 */
export const useTimelineDragPan = ({ contentRef }) => {
  const [isDragging, setIsDragging] = useState(false)
  const [atLeftEdge, setAtLeftEdge] = useState(false)
  const [atRightEdge, setAtRightEdge] = useState(false)

  const dragStartPos = useRef({ x: 0, y: 0 })
  const scrollStartPos = useRef({ left: 0, top: 0 })
  const moveHistory = useRef([])
  const rafRef = useRef(null)

  const cancelMomentum = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  const checkEdges = useCallback(() => {
    if (!contentRef.current) return
    const { scrollLeft, scrollWidth, clientWidth } = contentRef.current
    setAtLeftEdge(scrollLeft <= 0)
    setAtRightEdge(scrollLeft >= scrollWidth - clientWidth - 1)
  }, [contentRef])

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return

    const target = e.target
    const isInteractive =
      target.getAttribute('data-drag-handle') === 'true' ||
      target.getAttribute('data-interactive') === 'true' ||
      target.closest('[data-drag-handle="true"]') ||
      target.closest('[data-interactive="true"]')

    if (isInteractive) return

    cancelMomentum()
    setAtLeftEdge(false)
    setAtRightEdge(false)
    e.preventDefault()
    setIsDragging(true)

    dragStartPos.current = { x: e.clientX, y: e.clientY }
    scrollStartPos.current = {
      left: contentRef.current.scrollLeft,
      top: contentRef.current.scrollTop,
    }
    moveHistory.current = [{ x: e.clientX, y: e.clientY, t: performance.now() }]
  }, [contentRef, cancelMomentum])

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !contentRef.current) return

    e.preventDefault()

    const now = performance.now()
    moveHistory.current.push({ x: e.clientX, y: e.clientY, t: now })

    // Trim to the velocity sample window
    const cutoff = now - VELOCITY_SAMPLE_MS
    moveHistory.current = moveHistory.current.filter(p => p.t >= cutoff)

    const deltaX = dragStartPos.current.x - e.clientX
    const deltaY = dragStartPos.current.y - e.clientY
    contentRef.current.scrollLeft = scrollStartPos.current.left + deltaX
    contentRef.current.scrollTop = scrollStartPos.current.top + deltaY

    checkEdges()
  }, [isDragging, contentRef, checkEdges])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)

    const el = contentRef.current
    if (!el) return

    // Compute release velocity from recent movement history
    const history = moveHistory.current
    let vx = 0
    let vy = 0
    if (history.length >= 2) {
      const oldest = history[0]
      const newest = history[history.length - 1]
      const dt = newest.t - oldest.t
      if (dt > 0) {
        vx = ((oldest.x - newest.x) / dt) * 16  // normalize to px/frame at 60fps
        vy = ((oldest.y - newest.y) / dt) * 16
      }
    }
    moveHistory.current = []

    if (Math.abs(vx) <= MIN_VELOCITY && Math.abs(vy) <= MIN_VELOCITY) {
      setAtLeftEdge(false)
      setAtRightEdge(false)
      return
    }

    const animate = () => {
      vx *= FRICTION
      vy *= FRICTION

      if (Math.abs(vx) < MIN_VELOCITY && Math.abs(vy) < MIN_VELOCITY) {
        rafRef.current = null
        setAtLeftEdge(false)
        setAtRightEdge(false)
        return
      }

      const prevLeft = el.scrollLeft
      const prevTop = el.scrollTop
      el.scrollLeft += vx
      el.scrollTop += vy

      // Kill velocity component when hitting an edge
      if (el.scrollLeft === prevLeft && vx !== 0) vx = 0
      if (el.scrollTop === prevTop && vy !== 0) vy = 0

      checkEdges()
      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)
  }, [contentRef, checkEdges])

  // Attach global mouse move/up listeners when dragging
  useEffect(() => {
    if (!isDragging) return

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  // Cancel momentum on unmount
  useEffect(() => cancelMomentum, [cancelMomentum])

  const cursor = isDragging ? 'grabbing' : 'grab'

  return {
    isDragging,
    handleMouseDown,
    cursor,
    atLeftEdge,
    atRightEdge,
  }
}
